import React, { useState, useEffect } from 'react';
import {
  Heart,
  MessageCircle,
  Sparkles,
  Flag,
  Send,
  Shield,
  RefreshCw,
  Globe,
  Award,
  ChevronDown,
  ChevronUp,
  Target,
  BarChart3,
  Flame,
  Swords,
  Trophy,
  Church,
} from 'lucide-react';
import { ReportModal } from './ReportModal.tsx';
import { FriendButton } from './FriendButton.tsx';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { formatAchievementName } from '../utils/achievementFormat.ts';
import { formatDisplayNumber, getEvaluationTier } from '../config/scoringConfig.ts';
import { TrophyBadge } from './TrophyBadge.tsx';
import { RegistrationRequiredCard } from './RegistrationRequiredCard.tsx';

interface CommunityFeedViewProps {
  isRegistered?: boolean;
  currentUser: {
    uid: string;
    username: string;
    displayName?: string;
    avatarUrl?: string | null;
    country?: string;
    role?: string;
  };
  onNavigateToGuidelines?: () => void;
  onOpenGuidelines?: () => void;
  onNavigateToProfile?: (subTab?: string) => void;
  onOpenReport?: (target: {
    type: 'profile_picture' | 'feed_post' | 'feed_comment';
    id: string;
    targetUserUid: string;
    label?: string;
  }) => void;
  focusedPostId?: number | null;
  onClearFocusedPost?: () => void;
  onOpenUserProfile?: (uidOrUsername: string) => void;
}

export type FeedReactionType = 'like' | 'heart' | 'haha' | 'sad' | 'pray';

export interface FeedPost {
  id: number;
  userUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  country: string;
  postType?: 'scripture_milestone' | 'challenge_mode' | string;
  achievementCode: string;
  achievementTitle: string;
  score?: number;
  accuracy?: number;
  bestStreak?: number;
  xpGained?: number;
  userRank?: string;
  churchName?: string;
  content: string;
  reactions: Record<FeedReactionType, number>;
  userReaction?: FeedReactionType | null;
  commentCount: number;
  createdAt: string;
  comments?: FeedComment[];
}

export interface FeedComment {
  id: number;
  postId: number;
  userUid: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  content: string;
  createdAt: string;
}

const REACTION_CONFIG: Array<{
  type: FeedReactionType;
  label: string;
  emoji: string;
  activeColor: string;
}> = [
  { type: 'like', label: 'Like', emoji: '👍', activeColor: 'text-[#F97316] bg-[#F97316]/15 border-[#F97316]/40' },
  { type: 'heart', label: 'Heart', emoji: '❤️', activeColor: 'text-rose-600 bg-rose-950/40 border-rose-700' },
  { type: 'haha', label: 'Haha', emoji: '😂', activeColor: 'text-amber-600 bg-amber-950/40 border-amber-700' },
  { type: 'sad', label: 'Sad', emoji: '😢', activeColor: 'text-[#FAFAFA] bg-[#1C1C1C] border-[#404040]' },
  { type: 'pray', label: 'Pray', emoji: '🙏', activeColor: 'text-[#FAFAFA] bg-[#1C1C1C] border-[#404040]' },
];

export const CommunityFeedView: React.FC<CommunityFeedViewProps> = ({
  isRegistered = false,
  currentUser,
  onNavigateToGuidelines,
  onOpenGuidelines,
  onNavigateToProfile,
  onOpenReport,
  focusedPostId,
  onClearFocusedPost,
  onOpenUserProfile,
}) => {
  if (!isRegistered) {
    return (
      <RegistrationRequiredCard
        title="Join the Quick!Bible Community"
        description="Connect with Bible scholars worldwide. Share your Scripture milestones, celebrate achievements, challenge friends, and grow together in God's Word."
        onSignUp={() => onNavigateToProfile?.('auth_signup')}
        onSignIn={() => onNavigateToProfile?.('auth_signin')}
      />
    );
  }
  const triggerGuidelines = onOpenGuidelines || onNavigateToGuidelines;
  const getAuthHeaders = () => {
    const token = localStorage.getItem('qb_auth_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token && !token.startsWith('auth-active-') && !token.startsWith('verified-')) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openCommentsForPost, setOpenCommentsForPost] = useState<Record<number, boolean>>({});
  const [commentsByPost, setCommentsByPost] = useState<Record<number, FeedComment[]>>({});
  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [commentErrors, setCommentErrors] = useState<Record<number, string>>({});
  const [isSubmittingComment, setIsSubmittingComment] = useState<Record<number, boolean>>({});
  const [highlightedPostId, setHighlightedPostId] = useState<number | null>(null);

  // Reporting Modal state
  const [reportTarget, setReportTarget] = useState<{
    targetType: 'profile_picture' | 'feed_post' | 'feed_comment';
    targetId: string;
    targetUserUid: string;
    targetUsername?: string;
  } | null>(null);

  const triggerReport = (t: {
    targetType: 'profile_picture' | 'feed_post' | 'feed_comment';
    targetId: string;
    targetUserUid: string;
    targetUsername?: string;
  }) => {
    if (onOpenReport) {
      onOpenReport({
        type: t.targetType,
        id: t.targetId,
        targetUserUid: t.targetUserUid,
        label: t.targetUsername ? `@${t.targetUsername}` : undefined,
      });
    } else {
      setReportTarget(t);
    }
  };

  const loadFeed = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/feed/posts?uid=${encodeURIComponent(currentUser.uid)}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch {
      // Offline fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, [currentUser.uid]);

  useEffect(() => {
    if (!focusedPostId || posts.length === 0) return;
    const targetId = Number(focusedPostId);
    setHighlightedPostId(targetId);
    setOpenCommentsForPost((prev) => ({ ...prev, [targetId]: true }));

    if (!commentsByPost[targetId]) {
      fetch(`/api/feed/posts/${targetId}/comments`, {
        headers: getAuthHeaders(),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.comments) {
            setCommentsByPost((prev) => ({ ...prev, [targetId]: data.comments }));
          }
        })
        .catch(() => {});
    }

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="feed-post-${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onClearFocusedPost?.();
    }, 150);

    const clearHighlightTimer = setTimeout(() => {
      setHighlightedPostId((prev) => (prev === targetId ? null : prev));
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearTimeout(clearHighlightTimer);
    };
  }, [focusedPostId, posts.length]);

  const handleToggleReaction = async (postId: number, reactionType: FeedReactionType) => {
    soundEngine.playClick();
    // Optimistic UI update
    setPosts((prevPosts) =>
      prevPosts.map((p) => {
        if (p.id !== postId) return p;
        const currentRx = p.userReaction;
        const nextRx = currentRx === reactionType ? null : reactionType;
        const newCounts = { ...p.reactions };

        if (currentRx) {
          newCounts[currentRx] = Math.max(0, (newCounts[currentRx] || 0) - 1);
        }
        if (nextRx) {
          newCounts[nextRx] = (newCounts[nextRx] || 0) + 1;
        }

        return {
          ...p,
          userReaction: nextRx,
          reactions: newCounts,
        };
      })
    );

    try {
      const res = await fetch(`/api/feed/posts/${postId}/react`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          reactionType,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? { ...p, reactions: data.reactions, userReaction: data.userReaction }
              : p
          )
        );
      }
    } catch {
      // Handled gracefully
    }
  };

  const handleToggleComments = async (postId: number) => {
    soundEngine.playClick();
    const currentlyOpen = openCommentsForPost[postId];
    setOpenCommentsForPost((prev) => ({ ...prev, [postId]: !currentlyOpen }));

    if (!currentlyOpen && !commentsByPost[postId]) {
      try {
        const res = await fetch(`/api/feed/posts/${postId}/comments`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setCommentsByPost((prev) => ({ ...prev, [postId]: data.comments || [] }));
        }
      } catch {
        // Handled
      }
    }
  };

  const handleCommentSubmit = async (postId: number, e: React.FormEvent) => {
    e.preventDefault();
    const text = (commentInputs[postId] || '').trim();
    if (!text) return;

    if (/(https?:\/\/|www\.|\.com|\.org|\.net|\.io|\.gg)/i.test(text)) {
      setCommentErrors((prev) => ({
        ...prev,
        [postId]: 'Links are not allowed in community comments per Community Guidelines.',
      }));
      return;
    }
    if (text.length > 200) {
      setCommentErrors((prev) => ({
        ...prev,
        [postId]: 'Comments are limited to 200 characters.',
      }));
      return;
    }

    setCommentErrors((prev) => ({ ...prev, [postId]: '' }));
    setIsSubmittingComment((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await fetch(`/api/feed/posts/${postId}/comments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userUid: currentUser.uid,
          username: currentUser.username,
          userAvatar: currentUser.avatarUrl || '',
          content: text,
        }),
      });

      if (res.ok) {
        const resData = await res.json();
        const rawComment = resData.comment || resData;
        const newComment: FeedComment = {
          id: rawComment.id || Date.now(),
          postId,
          userUid: rawComment.userUid || currentUser.uid,
          username: rawComment.username || currentUser.username,
          displayName:
            rawComment.displayName || currentUser.displayName || currentUser.username,
          avatarUrl:
            rawComment.avatarUrl !== undefined
              ? rawComment.avatarUrl
              : currentUser.avatarUrl || '',
          content: rawComment.content || text,
          createdAt: rawComment.createdAt || new Date().toISOString(),
        };
        setCommentsByPost((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), newComment],
        }));
        setCommentInputs((prev) => ({ ...prev, [postId]: '' }));
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p
          )
        );
      }
    } catch {
      // Ignore
    } finally {
      setIsSubmittingComment((prev) => ({ ...prev, [postId]: false }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-6 sm:py-8 px-3.5 sm:px-6 space-y-6">
      {/* Top Banner with Guidelines Navigation */}
      <div className="rounded-3xl bg-[#161618] border border-[#27272A] text-white p-5 sm:p-7 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#F97316]/15 text-[#F97316] text-[11px] font-bold uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5" />
              Global Fellowship
            </span>
            <span className="text-xs text-[#A3A3A3]">Community Feed</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">
            Scripture Scholars Worldwide
          </h1>
          <p className="text-xs sm:text-sm text-[#D4D4D4] max-w-xl">
            Celebrate Scripture achievements, quiz milestones, and encouragement with believers around the globe.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={triggerGuidelines}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#404040] text-xs font-semibold text-white transition-colors cursor-pointer"
          >
            <Shield className="w-3.5 h-3.5 text-[#F97316]" />
            <span>Community Guidelines</span>
          </button>
          <button
            type="button"
            onClick={loadFeed}
            title="Refresh Feed"
            aria-label="Refresh Community Feed"
            className="p-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#404040] text-white transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Feed Posts List */}
      {isLoading && posts.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <div className="w-10 h-10 border-4 border-[#525252] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-[#A3A3A3] font-medium">Loading feed...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-3xl bg-[#141414] border border-[#262626] p-8 text-center space-y-3 shadow-xs">
          <Sparkles className="w-10 h-10 text-amber-500 mx-auto" />
          <h2 className="text-base font-bold text-[#FAFAFA]">
            No posts yet
          </h2>
          <p className="text-xs text-[#A3A3A3] max-w-md mx-auto">
            Finish a quiz and share your result to start the feed.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const isCommentsOpen = Boolean(openCommentsForPost[post.id]);
            const comments = commentsByPost[post.id] || [];
            const isChallenge =
              post.postType === 'challenge_mode' ||
              post.achievementCode === 'CHALLENGE_MODE' ||
              (post.achievementTitle &&
                post.achievementTitle.toLowerCase().includes('challenge'));

            return (
              <article
                key={post.id}
                data-testid={`feed-post-${post.id}`}
                className={`rounded-3xl bg-[#141414] border p-4 sm:p-6 shadow-xs space-y-3.5 transition-all ${
                  highlightedPostId === post.id
                    ? 'border-[#F97316] ring-1 ring-[#F97316]/40'
                    : 'border-[#262626]'
                }`}
              >
                {/* Enhanced Author Profile Header Row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {/* User profile picture or avatar initials */}
                    <div className="relative group/avatar shrink-0 mt-0.5">
                      <div
                        onClick={() => onOpenUserProfile && onOpenUserProfile(post.userUid)}
                        className="cursor-pointer hover:opacity-85 transition-opacity"
                        title={`View @${post.username}'s profile`}
                      >
                        {post.avatarUrl ? (
                          <img
                            src={post.avatarUrl}
                            alt={post.displayName}
                            className="w-11 h-11 rounded-2xl object-cover border border-[#262626] shadow-xs"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-2xl bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-sm flex items-center justify-center shadow-xs">
                            {post.username.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      {post.avatarUrl && (
                        <button
                          type="button"
                          onClick={() =>
                            triggerReport({
                              targetType: 'profile_picture',
                              targetId: String(post.id),
                              targetUserUid: post.userUid,
                              targetUsername: post.username,
                            })
                          }
                          title="Report Profile Picture"
                          aria-label={`Report profile picture of @${post.username}`}
                          className="absolute -top-1 -right-1 hidden group-hover/avatar:flex w-5 h-5 rounded-full bg-red-600 text-white items-center justify-center shadow-sm"
                        >
                          <Flag className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>

                    <div className="min-w-0 space-y-1">
                      {/* Name + Username + Country + Friend Button Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => onOpenUserProfile && onOpenUserProfile(post.userUid)}
                          className="font-bold text-sm text-[#FAFAFA] hover:text-amber-400 transition-colors truncate cursor-pointer text-left"
                        >
                          {post.displayName}
                        </button>
                        <button
                          type="button"
                          onClick={() => onOpenUserProfile && onOpenUserProfile(post.userUid)}
                          className="text-xs font-mono text-[#A3A3A3] hover:text-amber-400 transition-colors cursor-pointer"
                        >
                          @{post.username}
                        </button>
                        {post.country && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#1C1C1C] text-[#A3A3A3] border border-[#262626]">
                            {post.country}
                          </span>
                        )}
                        {currentUser?.uid && currentUser.uid !== post.userUid && (
                          <FriendButton
                            currentUserUid={currentUser.uid}
                            targetUserUid={post.userUid}
                            size="xs"
                          />
                        )}
                      </div>

                      {/* Rank & Church Name Badges (Gracefully hidden if absent) */}
                      {(post.userRank || post.churchName) && (
                        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                          {post.userRank && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[11px] font-semibold">
                              <Trophy className="w-3 h-3 text-amber-500 shrink-0" />
                              <span className="truncate">{post.userRank}</span>
                            </span>
                          )}

                          {post.churchName && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#1C1C1C] border border-[#262626] text-[#D4D4D4] text-[11px] max-w-[180px] sm:max-w-[260px] truncate">
                              <Church className="w-3 h-3 text-[#F97316] shrink-0" />
                              <span className="truncate">{post.churchName}</span>
                            </span>
                          )}
                        </div>
                      )}

                      <time className="text-[11px] text-[#A3A3A3] block pt-0.5">
                        {new Date(post.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </time>
                    </div>
                  </div>

                  {/* Report post */}
                  <button
                    type="button"
                    onClick={() =>
                      triggerReport({
                        targetType: 'feed_post',
                        targetId: String(post.id),
                        targetUserUid: post.userUid,
                        targetUsername: post.username,
                      })
                    }
                    title="Report post per Community Guidelines"
                    aria-label={`Report post by @${post.username}`}
                    className="p-1.5 rounded-lg text-[#A3A3A3] hover:text-red-500 hover:bg-[#1C1C1C] transition-colors shrink-0 cursor-pointer"
                  >
                    <Flag className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Distinctive Orange-Themed "Challenge Mode" or Amber "Scripture Milestone" Badge */}
                <div className="flex flex-wrap items-center gap-2">
                  {isChallenge ? (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-[#FF8A00]/15 border border-[#FF8A00]/30 text-[#FF8A00] text-xs font-bold shadow-xs">
                      <Swords className="w-3.5 h-3.5 text-[#FF8A00] shrink-0" />
                      <span>Challenge Mode</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-amber-500/12 border border-amber-500/20 text-[#FBBF24] text-xs font-semibold">
                      <Award className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span>{formatAchievementName(post.achievementTitle || post.achievementCode)}</span>
                    </div>
                  )}
                  {isChallenge && post.accuracy !== undefined && (() => {
                    const evalTier = getEvaluationTier({
                      accuracyPercent: post.accuracy,
                      totalQuestions: 8,
                    });
                    return (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-xl bg-[#1C1C1C] border border-[#262626] text-[11px] font-bold text-[#FAFAFA]">
                        {evalTier.trophy !== 'none' && (
                          <Trophy className="w-3 h-3 text-amber-400 shrink-0" />
                        )}
                        <span>{evalTier.label}</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Challenge Mode Post Statistics Card (4 Columns on Desktop, 2x2 on Mobile) */}
                {isChallenge && (
                  <div className="p-3.5 sm:p-4 rounded-2xl bg-[#18181A] border border-[#262626] shadow-sm my-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 divide-x-0 md:divide-x divide-y md:divide-y-0 divide-[#262626]">
                      {/* Column 1: Score */}
                      <div className="p-2 sm:p-3 text-center flex flex-col items-center justify-center">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] font-medium mb-1">
                          <Target className="w-3.5 h-3.5 text-[#A3A3A3] shrink-0" />
                          <span>Score</span>
                        </div>
                        <div className={`text-xl sm:text-2xl font-extrabold font-mono tabular-nums ${(post.score || 0) <= 0 ? 'text-[#737373]' : 'text-[#FAFAFA]'}`}>
                          {formatDisplayNumber(post.score || 0).text}
                        </div>
                      </div>

                      {/* Column 2: Accuracy */}
                      <div className="p-2 sm:p-3 text-center flex flex-col items-center justify-center">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] font-medium mb-1">
                          <BarChart3 className="w-3.5 h-3.5 text-[#10B981] shrink-0" />
                          <span>Accuracy</span>
                        </div>
                        <div className="text-xl sm:text-2xl font-extrabold font-mono tabular-nums text-[#10B981]">
                          {post.accuracy || 0}%
                        </div>
                      </div>

                      {/* Column 3: Best Streak */}
                      <div className="p-2 sm:p-3 text-center flex flex-col items-center justify-center">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] font-medium mb-1">
                          <Flame className="w-3.5 h-3.5 text-[#F59E0B] shrink-0" />
                          <span>Best Streak</span>
                        </div>
                        <div className={`text-xl sm:text-2xl font-extrabold font-mono tabular-nums ${(post.bestStreak || 0) <= 0 ? 'text-[#737373]' : 'text-[#F59E0B]'}`}>
                          {formatDisplayNumber(post.bestStreak || 0).text}
                        </div>
                      </div>

                      {/* Column 4: XP Gained */}
                      <div className="p-2 sm:p-3 text-center flex flex-col items-center justify-center">
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] font-medium mb-1">
                          <span className="px-1 py-0.2 rounded text-[9px] font-extrabold border border-[#737373] text-[#FAFAFA] leading-none">
                            XP
                          </span>
                          <span>XP Gained</span>
                        </div>
                        <div className={`text-xl sm:text-2xl font-extrabold font-mono tabular-nums ${(post.xpGained || 0) <= 0 ? 'text-[#737373]' : 'text-[#FAFAFA]'}`}>
                          {(post.xpGained || 0) <= 0 ? '–' : `+${formatDisplayNumber(post.xpGained || 0).text}`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reflection / User Text Message (Max 200 chars) */}
                {post.content && (
                  <p className="text-sm text-[#FAFAFA] leading-relaxed break-words font-sans">
                    {post.content}
                  </p>
                )}

                {/* Reactions Row: ONLY Like, Heart, Haha, Sad, Pray (NO ANGRY!) */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#262626]">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {REACTION_CONFIG.map((rx) => {
                      const count = post.reactions?.[rx.type] || 0;
                      const isActive = post.userReaction === rx.type;
                      return (
                        <button
                          key={rx.type}
                          type="button"
                          onClick={() => handleToggleReaction(post.id, rx.type)}
                          aria-label={`React with ${rx.label}`}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-medium border transition-all active:scale-95 cursor-pointer ${
                            isActive
                              ? rx.activeColor
                              : 'border-[#262626] bg-[#1C1C1C] text-[#D4D4D4] hover:border-[#404040]'
                          }`}
                        >
                          <span className="text-sm select-none">{rx.emoji}</span>
                          <span className="font-mono text-[11px] font-bold tabular-nums">
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Comments Toggle Button */}
                  <button
                    type="button"
                    onClick={() => handleToggleComments(post.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-medium text-[#D4D4D4] hover:bg-[#1C1C1C] transition-colors cursor-pointer"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-[#A3A3A3]" />
                    <span>
                      {post.commentCount} Comment{post.commentCount === 1 ? '' : 's'}
                    </span>
                    {isCommentsOpen ? (
                      <ChevronUp className="w-3 h-3 text-[#A3A3A3]" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-[#A3A3A3]" />
                    )}
                  </button>
                </div>

                {/* Comments Accordion */}
                {isCommentsOpen && (
                  <div className="pt-3 border-t border-[#262626] space-y-3">
                    {/* Existing comments */}
                    {comments.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                        {comments.map((comment) => {
                          const commentInitials = (
                            comment.username ||
                            comment.displayName ||
                            'SC'
                          )
                            .slice(0, 2)
                            .toUpperCase();
                          const resolvedCommentAvatar =
                            comment.avatarUrl ||
                            (comment.userUid === currentUser.uid
                              ? currentUser.avatarUrl || ''
                              : '');

                          return (
                            <div
                              key={comment.id}
                              data-testid={`feed-comment-${comment.id}`}
                              className="p-2.5 rounded-xl bg-[#1C1C1C] border border-[#262626] text-xs flex items-start justify-between gap-2.5"
                            >
                              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                {/* 32px circular commenter avatar with dark-gray initials fallback */}
                                <div
                                  onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                  className="cursor-pointer shrink-0 hover:opacity-85 transition-opacity"
                                  title={`View @${comment.username}'s profile`}
                                >
                                  {resolvedCommentAvatar ? (
                                    <img
                                      src={resolvedCommentAvatar}
                                      alt={comment.displayName || comment.username}
                                      className="w-8 h-8 rounded-full object-cover border border-[#262626] shrink-0"
                                    />
                                  ) : (
                                    <div
                                      aria-label={`${comment.displayName || comment.username} avatar initials`}
                                      className="w-8 h-8 rounded-full bg-[#1C1C1C] border border-[#262626] text-[#A3A3A3] font-bold text-xs flex items-center justify-center shrink-0"
                                    >
                                      {commentInitials}
                                    </div>
                                  )}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 font-semibold text-[#FAFAFA] flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                      className="truncate font-semibold text-[#FAFAFA] hover:text-amber-400 transition-colors cursor-pointer text-left"
                                    >
                                      {comment.displayName}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => onOpenUserProfile && onOpenUserProfile(comment.userUid)}
                                      className="font-normal font-mono text-[10px] text-[#A3A3A3] hover:text-amber-400 transition-colors cursor-pointer"
                                    >
                                      @{comment.username}
                                    </button>
                                    {currentUser?.uid && currentUser.uid !== comment.userUid && (
                                      <FriendButton
                                        currentUserUid={currentUser.uid}
                                        targetUserUid={comment.userUid}
                                        size="xs"
                                      />
                                    )}
                                  </div>
                                  <p className="text-[#D4D4D4] mt-0.5 break-words">
                                    {comment.content}
                                  </p>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  triggerReport({
                                    targetType: 'feed_comment',
                                    targetId: String(comment.id),
                                    targetUserUid: comment.userUid,
                                    targetUsername: comment.username,
                                  })
                                }
                                title="Report comment"
                                aria-label={`Report comment by @${comment.username}`}
                                className="text-[#A3A3A3] hover:text-red-500 p-1 shrink-0 cursor-pointer"
                              >
                                <Flag className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-[#A3A3A3] italic">
                        Be the first to leave an edifying comment!
                      </p>
                    )}

                    {commentErrors[post.id] && (
                      <p className="text-[11px] text-amber-400 font-medium">
                        {commentErrors[post.id]}
                      </p>
                    )}

                    {/* New comment form (max 200 chars) */}
                    <form
                      onSubmit={(e) => handleCommentSubmit(post.id, e)}
                      className="flex gap-2"
                    >
                      <input
                        type="text"
                        maxLength={200}
                        placeholder="Write a comment (up to 200 chars, text-only)..."
                        value={commentInputs[post.id] || ''}
                        onChange={(e) =>
                          setCommentInputs((prev) => ({
                            ...prev,
                            [post.id]: e.target.value,
                          }))
                        }
                        className="flex-1 rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-xs text-[#FAFAFA] focus:outline-none focus:ring-2 focus:ring-[#525252]"
                      />
                      <button
                        type="submit"
                        disabled={
                          isSubmittingComment[post.id] ||
                          !(commentInputs[post.id] || '').trim()
                        }
                        className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-all shrink-0 cursor-pointer"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>Reply</span>
                      </button>
                    </form>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Global Report Modal */}
      {reportTarget && (
        <ReportModal
          isOpen={true}
          onClose={() => setReportTarget(null)}
          reporterUid={currentUser.uid}
          reporterUsername={currentUser.username}
          targetType={reportTarget.targetType}
          targetId={reportTarget.targetId}
          targetUserUid={reportTarget.targetUserUid}
          targetUsername={reportTarget.targetUsername}
        />
      )}
    </div>
  );
};
